/**
 * The ONLY place where a business state becomes a colour and a label.
 * The front end computes no state: it displays what the API decided.
 */
import type { Decision, SignalState, SourceHealth, SourceStatus } from "./api/client";
import { formatTime } from "./format";

export type Tone = "green" | "amber" | "red" | "neutral";

interface StateView {
  tone: Tone;
  label: string;
  description: string;
}

export const STATES: Record<SignalState, StateView> = {
  clear: {
    tone: "green",
    label: "Aucune correspondance",
    description: "La source a été consultée et ne contient pas l'identité recherchée.",
  },
  possible_match: {
    tone: "amber",
    label: "À examiner",
    description: "Nom proche, homonymie possible ou résultats non détaillés : vérification manuelle.",
  },
  strong_match: {
    tone: "red",
    label: "Correspondance forte",
    description: "Nom, prénom et, si fournie, date de naissance concordent : dossier à analyser.",
  },
  unavailable: {
    tone: "neutral",
    label: "Indisponible",
    description: "La source n'a pas répondu. Ce n'est pas une absence de résultat.",
  },
  not_checked: {
    tone: "neutral",
    label: "Non vérifié",
    description: "Source non interrogeable automatiquement : vérifier via le lien proposé.",
  },
};

export const stateView = (state: SignalState): StateView => STATES[state];

export const DECISIONS: Record<Decision, { label: string; tone: Tone }> = {
  compliant: { label: "Conforme", tone: "green" },
  to_review: { label: "À examiner", tone: "amber" },
  non_compliant: { label: "Non conforme", tone: "red" },
};

/** Live availability of a source (from /api/sources), as shown before searching. */
export const SOURCE_STATUS: Record<SourceStatus, { label: string; tone: Tone; problem: boolean }> = {
  ok: { label: "Disponible", tone: "green", problem: false },
  unknown: { label: "Pas encore interrogée", tone: "neutral", problem: false },
  not_configured: { label: "Non configurée", tone: "neutral", problem: false },
  degraded: { label: "Instable", tone: "amber", problem: true },
  down: { label: "En panne", tone: "red", problem: true },
  paused: { label: "En pause", tone: "amber", problem: true },
};

export function healthSummary(health: SourceHealth): string {
  if (health.status === "paused" && health.paused_until) {
    return `en pause jusqu'à ${formatTime(health.paused_until)} (limitation de débit)`;
  }
  if (health.status === "down") {
    return `${health.consecutive_failures} échecs de suite${health.last_error ? ` : ${health.last_error}` : ""}`;
  }
  if (health.status === "degraded" && health.success_rate !== null) {
    return `${Math.round(health.success_rate * 100)} % de réponses sur les derniers appels`;
  }
  return SOURCE_STATUS[health.status].label;
}

export const badgeClass = (tone: Tone) => `badge badge-${tone}`;
